/**
 * CoachmarkOverlay — first-run guided walkthrough for one flow (WO-H1).
 *
 * Shows once per device for a flowKey (capture-receipt / build-estimate / send-invoice), then
 * never again unless replayed from the Help Center. Skippable at every step and resumable
 * (progress isn't lost mid-step because it only marks "seen" on finish/skip).
 *
 * v1 note: presents stepped intro cards centered over a dim scrim. Per-control anchoring/spotlight
 * (using Coachmark.anchorKey) is the H1-P2 enhancement; the content + once-only behavior are full here.
 *
 * The primary button uses brand indigo (action.primary) — copper stays reserved for the single
 * owner-authority action per screen (invariant 6).
 */
import React, { useEffect, useState } from 'react';
import { Modal, Pressable, View, StyleSheet } from 'react-native';
import { Text } from '../ui';
import { theme } from '../../theme';
import { useHelp } from '../../help';

export interface CoachmarkOverlayProps {
  flowKey: string;
  /** Gate auto-show on a condition (e.g. data loaded). Defaults to true. */
  enabled?: boolean;
}

export function CoachmarkOverlay({ flowKey, enabled = true }: CoachmarkOverlayProps) {
  const help = useHelp();
  const flow = help.getFlow(flowKey);
  const [index, setIndex] = useState(0);
  const [open, setOpen] = useState(false);

  // Auto-show first run only, once the persisted seen-set has loaded (no flash).
  useEffect(() => {
    if (!enabled) return;
    if (!help.seenLoaded) return;
    if (flow.seen) return;
    if (flow.steps.length === 0) return;
    setOpen(true);
  }, [enabled, help.seenLoaded, flow.seen, flow.steps.length]);

  const finish = () => {
    help.markSeen(flowKey);
    setOpen(false);
  };

  if (!open) return null;

  const step = flow.steps[index];
  const isLast = index >= flow.steps.length - 1;

  return (
    <Modal visible transparent animationType="fade" onRequestClose={finish}>
      <View style={styles.root}>
        <Pressable style={styles.scrim} onPress={finish} accessibilityLabel="Skip the walkthrough" />
        <View style={styles.card}>
          <Text variant="label" color="secondary">
            {`STEP ${index + 1} OF ${flow.steps.length}`}
          </Text>
          <Text variant="title">{step.title}</Text>
          <Text variant="body" color="secondary">
            {step.body}
          </Text>

          <View style={styles.actions}>
            <Pressable
              onPress={finish}
              accessibilityRole="button"
              accessibilityLabel="Skip"
              style={styles.skip}
            >
              <Text variant="bodyStrong" color="secondary">
                Skip
              </Text>
            </Pressable>

            <View style={styles.rightActions}>
              {index > 0 && (
                <Pressable
                  onPress={() => setIndex((i) => Math.max(0, i - 1))}
                  accessibilityRole="button"
                  accessibilityLabel="Back"
                  style={styles.back}
                >
                  <Text variant="bodyStrong" color="link">
                    Back
                  </Text>
                </Pressable>
              )}
              <Pressable
                onPress={() => (isLast ? finish() : setIndex((i) => i + 1))}
                accessibilityRole="button"
                accessibilityLabel={isLast ? 'Got it' : 'Next'}
                style={styles.primary}
              >
                <Text variant="bodyStrong" color="onPrimary">
                  {isLast ? 'Got it' : 'Next'}
                </Text>
              </Pressable>
            </View>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  root: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: theme.spacing.xl },
  scrim: { ...StyleSheet.absoluteFillObject, backgroundColor: theme.color.scrim },
  card: {
    width: '100%',
    maxWidth: 420,
    backgroundColor: theme.color.bg.surface,
    borderRadius: theme.radius.lg,
    borderWidth: theme.layout.hairline,
    borderColor: theme.color.border.default,
    padding: theme.spacing.xl,
    gap: theme.spacing.md,
  },
  actions: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: theme.spacing.sm },
  rightActions: { flexDirection: 'row', alignItems: 'center', gap: theme.spacing.sm },
  skip: { minHeight: theme.layout.minTapTarget, justifyContent: 'center', paddingHorizontal: theme.spacing.sm },
  back: { minHeight: theme.layout.minTapTarget, justifyContent: 'center', paddingHorizontal: theme.spacing.md },
  primary: {
    minHeight: theme.layout.minTapTarget,
    justifyContent: 'center',
    paddingHorizontal: theme.spacing.xl,
    backgroundColor: theme.color.action.primary,
    borderRadius: theme.radius.md,
  },
});
